import React, { createContext, useEffect, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'webip.theme';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export type ThemeContextValue = {
    themePreference: ThemePreference;
    setThemePreference: (theme: ThemePreference) => void;
    resolvedTheme: ResolvedTheme;
};

const VALID_THEMES: ThemePreference[] = ['system', 'light', 'dark'];

const isThemePreference = (value: unknown): value is ThemePreference =>
    typeof value === 'string' && VALID_THEMES.includes(value as ThemePreference);

const parseStoredThemePreference = (value: string | null): ThemePreference | null => {
    if (!value) return null;
    if (isThemePreference(value)) return value;

    try {
        const parsed = JSON.parse(value);
        return isThemePreference(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const getSystemTheme = (): ResolvedTheme => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialThemePreference = (): ThemePreference => {
    if (typeof document !== 'undefined') {
        const themePreference = document.documentElement.dataset.themePreference;
        if (isThemePreference(themePreference)) {
            return themePreference;
        }
    }

    if (typeof window !== 'undefined') {
        return parseStoredThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY)) ?? 'system';
    }

    return 'system';
};

const setDocumentTheme = (themePreference: ThemePreference, resolvedTheme: ResolvedTheme) => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    root.dataset.themePreference = themePreference;
    root.style.colorScheme = resolvedTheme;

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute('content', resolvedTheme === 'dark' ? '#0e1320' : '#f7f8fb');
    }
};

export const ThemeContext = createContext<ThemeContextValue>({
    themePreference: 'system',
    setThemePreference: () => { },
    resolvedTheme: 'light',
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => getInitialThemePreference());
    const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

    const resolvedTheme = themePreference === 'system' ? systemTheme : themePreference;

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const syncSystemTheme = (event?: MediaQueryListEvent) => {
            setSystemTheme(event?.matches ?? mediaQuery.matches ? 'dark' : 'light');
        };

        syncSystemTheme();

        mediaQuery.addEventListener('change', syncSystemTheme);
        return () => mediaQuery.removeEventListener('change', syncSystemTheme);
    }, []);

    useEffect(() => {
        setDocumentTheme(themePreference, resolvedTheme);

        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
        } catch {
            // ignore storage failures and keep the in-memory preference
        }
    }, [resolvedTheme, themePreference]);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== THEME_STORAGE_KEY) return;

            const nextPreference = parseStoredThemePreference(event.newValue);
            if (nextPreference) {
                setThemePreferenceState(nextPreference);
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const value = useMemo<ThemeContextValue>(() => ({
        themePreference,
        setThemePreference: setThemePreferenceState,
        resolvedTheme,
    }), [resolvedTheme, themePreference]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};